(() => {
  if (window.VelvetCart) return;
  const config = document.querySelector('[data-cart-config]');
  if (!config) return;
  const root = config.dataset.root;
  const surfaces = () => [...document.querySelectorAll('[data-cart-section]')];
  const drawer = () => document.querySelector('velvet-cart-drawer');
  let queue = Promise.resolve(), pending = 0, opener, editTimer, editedInput, focusAfterUpdate, hasError = false;
  function counts(value) {
    if (!Number.isInteger(value)) return;
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = String(value); });
  }
  function busy(on) {
    surfaces().forEach(surface => {
      surface.setAttribute('aria-busy', String(on));
      surface.querySelectorAll('[data-cart-step], [data-cart-quantity], [data-cart-checkout], [data-cart-note]').forEach(el => { el.disabled = on; });
      surface.querySelectorAll('[data-cart-remove]').forEach(el => el.setAttribute('aria-disabled', String(on)));
    });
    if (!on && focusAfterUpdate?.isConnected) {
      focusAfterUpdate.focus({ preventScroll: true });
      focusAfterUpdate = null;
    }
  }
  function message(text) {
    hasError = true;
    surfaces().forEach(surface => {
      const error = surface.querySelector('[data-cart-error]');
      if (!error) return;
      error.replaceChildren(document.createTextNode(text + ' '));
      const retry = document.createElement('button');
      retry.type = 'button'; retry.dataset.cartRetry = ''; retry.textContent = config.dataset.retry;
      error.append(retry); error.hidden = false;
    });
  }
  function enqueue(task) {
    pending++; busy(true);
    const result = queue.then(async () => {
      surfaces().forEach(s => { const e = s.querySelector('[data-cart-error]'); if (e) e.hidden = true; });
      return task();
    });
    queue = result.catch(() => {}).finally(() => { pending--; if (!pending) busy(false); });
    return result;
  }
  function context() {
    return { sections: surfaces().map(s => s.dataset.cartSection).slice(0, 5), sections_url: root + 'cart' };
  }
  async function sections() {
    const url = new URL(root + 'cart', location.origin);
    url.searchParams.set('sections', context().sections.join(','));
    url.searchParams.set('_vct', String(Date.now()));
    // Shopify's /cart route switches to the Ajax cart object when JSON is requested.
    // Let the sections query control the response so it returns rendered section HTML.
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(config.dataset.refreshError);
    return response.json();
  }
  function render(htmls) {
    const replacements = [];
    for (const surface of surfaces()) {
      const html = htmls?.[surface.dataset.cartSection];
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-cart-section]');
      const content = replacement?.querySelector('[data-cart-content]');
      if (!content) return false;
      replacements.push({ surface, replacement, content });
    }
    hasError = false;
    const active = document.activeElement;
    const activeSurface = active?.closest('[data-cart-section]');
    const activeLine = active?.closest('[data-cart-line]');
    const focus = activeSurface ? { id: activeSurface.dataset.cartSection, key: activeLine?.dataset.lineKey, index: +(activeLine?.dataset.lineIndex || 0), action: active?.dataset.focusAction, close: active?.hasAttribute('data-cart-close') } : null;
    for (const { surface, replacement, content } of replacements) {
      const scroll = surface.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      surface.querySelector('[data-cart-content]').replaceChildren(...content.childNodes);
      surface.dataset.cartCountValue = replacement.dataset.cartCountValue;
      const scroller = surface.querySelector('[data-cart-scroll]');
      if (scroller) scroller.scrollTop = scroll;
      counts(Number(replacement.dataset.cartCountValue));
      const status = surface.querySelector('[data-cart-status]');
      if (status) status.textContent = config.dataset.updated;
    }
    if (focus) {
      const surface = surfaces().find(s => s.dataset.cartSection === focus.id);
      const lines = [...surface.querySelectorAll('[data-cart-line]')];
      const line = lines.find(l => l.dataset.lineKey === focus.key) || lines[Math.min(focus.index, lines.length - 1)];
      const target = focus.action ? line?.querySelector(`[data-focus-action="${focus.action}"]`) : focus.close ? surface.querySelector('[data-cart-close]') : null;
      focusAfterUpdate = target || surface.querySelector('[data-cart-close], .cart-page-empty a');
    }
    busy(pending > 0);
    return true;
  }
  async function synchronize(htmls) {
    if (render(htmls)) return true;
    try { if (render(await sections())) return true; } catch { /* Retry only the read, never the mutation. */ }
    message(config.dataset.refreshError);
    return false;
  }
  async function getCart() {
    const response = await fetch(root + 'cart.js?_vct=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(config.dataset.refreshError);
    return response.json();
  }
  async function mutate(endpoint, body, options = {}) {
    return enqueue(async () => {
      let response, data;
      try {
        response = await fetch(root + 'cart/' + endpoint + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, ...context() }) });
        data = await response.json();
      } catch {
        // A lost response may follow a successful mutation. Reconcile before allowing a retry.
        await synchronize();
        try { counts((await getCart()).item_count); } catch { /* Show the uncertainty explicitly. */ }
        message(config.dataset.refreshError);
        throw new Error(config.dataset.refreshError);
      }
      if (!response.ok || data.status) {
        await synchronize();
        const reason = typeof data.description === 'string' ? data.description : config.dataset.error;
        message(reason); throw new Error(reason);
      }
      let cart = endpoint === 'add' ? null : data;
      const synced = await synchronize(data.sections);
      if (!cart) { try { cart = await getCart(); } catch { /* Rendered section count still reflects the successful add. */ } }
      counts(cart?.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'velvet-cart' } }));
      if (options.open && drawer()?.dataset.autoOpen === 'true') open(options.opener, false);
      if (!synced) message(config.dataset.refreshError);
      return data;
    });
  }
  async function refresh() {
    return enqueue(async () => {
      try {
        const [htmls, cart] = await Promise.all([sections(), getCart()]);
        if (!render(htmls)) throw new Error(config.dataset.refreshError);
        counts(cart.item_count);
        return cart;
      } catch (error) { message(config.dataset.refreshError); throw error; }
    });
  }
  function expanded(on) {
    document.querySelectorAll('[data-cart-open]').forEach(el => el.setAttribute('aria-expanded', String(on)));
  }
  function open(trigger, reload = true) {
    const surface = drawer(), dialog = surface?.querySelector('dialog');
    if (!dialog || surface.dataset.enabled !== 'true') return false;
    if (!dialog.open) {
      document.querySelectorAll('velvet-header dialog[open]').forEach(d => d.close());
      opener = trigger?.isConnected ? trigger : document.activeElement;
      dialog.showModal(); expanded(true);
      surface.querySelector('[data-cart-close]')?.focus();
    }
    if (reload) refresh().catch(() => {});
    return true;
  }
  function close() { drawer()?.querySelector('dialog[open]')?.close(); }
  document.addEventListener('close', event => {
    if (!event.target.matches('.cart-drawer')) return;
    expanded(false);
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0 && open(trigger)) { event.preventDefault(); return; }
    if (event.target.closest('[data-cart-close]')) { close(); return; }
    if (event.target.matches('.cart-drawer')) {
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close();
      return;
    }
    if (event.target.closest('[data-cart-retry]')) { refresh().catch(() => {}); return; }
    const control = event.target.closest('[data-cart-step], [data-cart-remove]');
    if (!control) return;
    event.preventDefault();
    if (pending) return;
    const line = control.closest('[data-cart-line]'), input = line.querySelector('[data-cart-quantity]');
    let quantity = 0;
    if (control.hasAttribute('data-cart-step')) {
      const minimum = +input.dataset.minimum || 1, step = +input.step || 1;
      quantity = +input.value + +control.dataset.cartStep * step;
      if (quantity < minimum) quantity = +control.dataset.cartStep < 0 ? 0 : minimum;
    }
    mutate('change', { id: line.dataset.lineKey, quantity }).catch(() => {});
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) {
      clearTimeout(editTimer); editedInput = null;
      if (pending) return;
      const min = +input.dataset.minimum || 1, step = +input.step || 1;
      let quantity = Number.isFinite(+input.value) ? Math.max(0, Math.floor(+input.value)) : min;
      if (quantity) quantity = min + Math.max(0, Math.floor((quantity - min) / step)) * step;
      mutate('change', { id: input.closest('[data-cart-line]').dataset.lineKey, quantity }).catch(() => {});
    }
    if (input.matches('[data-cart-note]') && !pending) mutate('update', { note: input.value }).catch(() => {});
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-cart-quantity]')) return;
    clearTimeout(editTimer);
    editedInput = event.target;
    editTimer = setTimeout(() => {
      const input = editedInput; editedInput = null;
      if (input?.isConnected) input.dispatchEvent(new Event('change', { bubbles: true }));
    }, 450);
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;
    event.preventDefault();
    if (event.submitter?.hasAttribute('data-cart-checkout')) {
      if (editedInput?.isConnected) editedInput.dispatchEvent(new Event('change', { bubbles: true }));
      queue.then(() => { if (!hasError) location.assign(root + 'checkout'); });
    }
  });
  // Refresh on browser-history restores and external cart updates without dispatch loops.
  window.addEventListener('pageshow', () => { refresh().catch(() => {}); });
  let wasHidden = document.hidden;
  document.addEventListener('visibilitychange', () => {
    if (wasHidden && !document.hidden && !pending) refresh().catch(() => {});
    wasHidden = document.hidden;
  });
  document.addEventListener('cart:updated', event => { if (event.detail?.source !== 'velvet-cart') refresh().catch(() => {}); });
  if (window.Shopify?.designMode) {
    ['shopify:section:select', 'shopify:block:select'].forEach(type => document.addEventListener(type, event => {
      if (event.target.closest('[data-cart-surface="drawer"]') || event.target.querySelector?.('[data-cart-surface="drawer"]')) open(null, false);
    }));
    document.addEventListener('shopify:section:load', () => { const state = drawer(); if (state) counts(+state.dataset.cartCountValue); });
  }
  window.VelvetCart = { add: (items, options = {}) => mutate('add', { items }, { open: true, ...options }), change: (key, quantity) => mutate('change', { id: key, quantity }), refresh, open, close, whenIdle: () => queue };
  const initial = drawer() || surfaces()[0];
  if (initial) counts(Number(initial.dataset.cartCountValue));
  if (document.readyState === 'complete') refresh().catch(() => {});
})();
